import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
// Internal imports
import styles from "./LeftPanelLayout.module.scss";
import appLogo from "../../assets/icons/app-logo.svg";
import en from "../../locales/en.json";
import { protectedRoutes } from "../../utils/protectedRoutes";
import {
  adminIcon,
  campaignIcon,
  clientsIcon,
  dashboardIcon,
  giftIcon,
  logoutIcon,
  profileIcon,
  reportingIcon,
  rewardIcon,
  // settingsIcon,
  surveyIcon,
  userIcon,
  usersIcon,
} from "../../utils/appIcons";
import { MenuItem, MenuListProps } from "./types";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../api/axiosInstance";
import endpoints from "../../api/endpoints";
import { toast } from "react-toastify";
import { saveLogout } from "../../features/auth/authSlice";
import plusIcon from "../../assets/icons/plus.svg";
import minusIcon from "../../assets/icons/minus.svg";

const MenuList: React.FC<MenuListProps & { activeSubMenu: string | null }> = ({
  items,
  expandedMenu,
  toggleMenu,
  activeSubMenu,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const logoutHandler = async () => {
    try {
      await axiosInstance.post(endpoints.auth.logout, {});
      // Clear token and refreshToken from cookies
      document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";
      document.cookie = "refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";
      // Clear user data from Redux store
      dispatch(saveLogout());
      // Redirect to login page
      navigate("/login");
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Logout failed. Please try again."
      );
    }
  };

  return (
    <ul>
      {items.map((item, index) => (
        <li key={`${index}-${item.label}`}>
          {item.subMenu ? (
            <div>
              <button
                className={`${styles.menuItem} ${
                  expandedMenu === item.to && !activeSubMenu && styles["active"]
                } btnTextOnly`}
                onClick={() => toggleMenu(item.to)}
              >
                <div className={styles.menuItemContent}>
                  {typeof item.icon === "function" && item.icon()}
                  <p>{item.label}</p>
                </div>

                <img
                  src={expandedMenu === item.to ? minusIcon : plusIcon}
                  alt="toggle"
                />
              </button>
              {expandedMenu === item.to && (
                <ul className={styles["sub-menu"]}>
                  {item.subMenu.map((subItem, subIndex) => (
                    <li key={`${subIndex}-${subItem.label}`}>
                      <NavLink
                        to={subItem.to}
                        className={`${styles.menuItemLink} ${
                          activeSubMenu === subItem.to && styles["active"]
                        }`}
                        onClick={() => toggleMenu(item.to, subItem.to)}
                      >
                        {subItem.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <NavLink
              to={item.to}
              className={`${styles.menuItemLink} ${
                expandedMenu === item.to && styles["active"]
              }`}
              onClick={(e) => {
                if (item.to === "/login") {
                  e.preventDefault(); // Prevent navigation
                  logoutHandler();
                } else {
                  toggleMenu(item.to);
                }
              }}
            >
              {typeof item.icon === "function" && item.icon()}
              <p>{item.label}</p>
            </NavLink>
          )}
        </li>
      ))}
    </ul>
  );
};

const SidebarMenu: React.FC = () => {
  const navigate = useNavigate();
  const { admin } = useSelector((state: any) => state.auth);
  const currentPath = window?.location?.pathname || "";
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);

  const toggleMenu = (menu: string, subMenu?: string) => {
    if (subMenu) {
      setActiveSubMenu(subMenu);
    } else {
      // Don't toggle off, just set the active menu
      setExpandedMenu(menu);
      setActiveSubMenu(null);
    }
  };

  // Filter menu items based on roles
  const filterMenuItems = (items: MenuItem[]) =>
    items.filter((item) => {
      if (!item.tag) return true;
      // Check if the user has at least one of the required roles
      return admin?.permissions?.some(
        (permission: string) => permission === item.tag
      );
    });

  const generalMenuItems: MenuItem[] = filterMenuItems([
    {
      to: "/dashboard",
      icon: dashboardIcon,
      alt: en.sidebar.dashboard,
      label: en.sidebar.dashboard,
      tag: protectedRoutes?.find((route) => route.path === "/dashboard")?.tag,
      subMenu: [
        {
          to: "/dashboard/overview",
          icon: "",
          alt: "",
          label: en.sidebar.dashboardOverview2,
          tag: protectedRoutes?.find((route) => route.path === "/overview")
            ?.tag,
        },
        // {
        //   to: "/dashboard/campaign-effects",
        //   icon: "",
        //   alt: "",
        //   label: en.sidebar.dashboardCampaignEffects,
        //   tag: protectedRoutes?.find(
        //     (route) => route.path === "/campaign-effects"
        //   )?.tag,
        // },
        {
          to: "/dashboard/shop-lift",
          icon: "",
          alt: "",
          label: en.sidebar.dashboardShopLift,
          tag: protectedRoutes?.find(
            (route) => route.path === "/shop-lift"
          )?.tag,
        },
        {
          to: "/dashboard/brand-lift",
          icon: "",
          alt: "",
          label: en.sidebar.dashboardBrandLift,
          tag: protectedRoutes?.find(
            (route) => route.path === "/brand-lift"
          )?.tag,
        },
        {
          to: "/dashboard/receipt",
          icon: "",
          alt: "",
          label: en.sidebar.dashboardReceipt,
          tag: protectedRoutes?.find(
            (route) => route.path === "/receipt"
          )?.tag,
        },
        {
          to: "/dashboard/survey-insights",
          icon: "",
          alt: "",
          label: en.sidebar.dashboardSurveyInsights,
          tag: protectedRoutes?.find(
            (route) => route.path === "/survey-insights"
          )?.tag,
        },
        // {
        //   to: "/dashboard/campaign-reach",
        //   icon: "",
        //   alt: "",
        //   label: en.sidebar.dashboardCampaignReach,
        //   tag: protectedRoutes?.find(
        //     (route) => route.path === "/campaign-reach"
        //   )?.tag,
        // },
      ],
    },
    {
      to: "/rewards",
      icon: rewardIcon,
      alt: en.sidebar.reward,
      label: en.sidebar.reward,
      tag: protectedRoutes?.find((route) => route.path === "/rewards")?.tag,
      subMenu: [
        {
          to: "/rewards/reward-settings",
          icon: "",
          alt: "",
          label: en.sidebar.rewardSettings,
          tag: protectedRoutes?.find(
            (route) => route.path === "/current-ranking"
          )?.tag,
        },
        {
          to: "/rewards/current-ranking",
          icon: "",
          alt: "",
          label: en.sidebar.currentRanking,
          tag: protectedRoutes?.find(
            (route) => route.path === "/current-ranking"
          )?.tag,
        },
        {
          to: "/rewards/point-history",
          icon: "",
          alt: "",
          label: en.sidebar.pointHistory,
          tag: protectedRoutes?.find((route) => route.path === "/point-history")
            ?.tag,
        },
      ],
    },
    {
      to: "/gift-shop",
      icon: giftIcon,
      alt: en.sidebar.giftShop,
      label: `${en.sidebar.giftShop}/ ${en.sidebar.cards}`,
      tag: protectedRoutes?.find((route) => route.path === "/gift-shop")?.tag,
      subMenu: [
        {
          to: "/gift-shop/gift-cards",
          icon: "",
          alt: "",
          label: en.sidebar.giftCardInventory,
          tag: protectedRoutes?.find((route) => route.path === "/gift-card")
            ?.tag,
        },
        {
          to: "/gift-shop/order-history",
          icon: "",
          alt: "",
          label: en.giftCard.orderHistory,
          tag: protectedRoutes?.find((route) => route.path === "/order-history")
            ?.tag,
        },
      ],
    },
    {
      to: "/reporting",
      icon: reportingIcon,
      alt: en.sidebar.reporting,
      label: en.sidebar.reporting,
      tag: protectedRoutes?.find((route) => route.path === "/reporting")?.tag,
      subMenu: [
        {
          to: "/reporting/location-matching-report",
          icon: "",
          alt: "",
          label: en.sidebar.locationMatchingReport,
          tag: protectedRoutes?.find(
            (route) => route.path === "/location-matching-report"
          )?.tag,
        },
        {
          to: "/reporting/audio-matching-report",
          icon: "",
          alt: "",
          label: en.sidebar.audioMatchingReport,
          tag: protectedRoutes?.find(
            (route) => route.path === "/audio-matching-report"
          )?.tag,
        },
        {
          to: "/reporting/store-matching-report",
          icon: "",
          alt: "",
          label: "Store Matching Report",
          tag: protectedRoutes?.find(
            (route) => route.path === "/store-matching-report"
          )?.tag,
        },
      ],
    },
  ]);

  const managementMenuItems: MenuItem[] = filterMenuItems([
    {
      to: "/users",
      icon: usersIcon,
      alt: en.sidebar.userManagement,
      label: en.sidebar.userManagement,
      tag: protectedRoutes?.find((route) => route.path === "/users")?.tag,
    },
    {
      to: "/admins",
      icon: adminIcon,
      alt: en.sidebar.adminManagement,
      label: en.sidebar.adminManagement,
      tag: protectedRoutes?.find((route) => route.path === "/admins")?.tag,
    },
    {
      to: "/roles",
      icon: userIcon,
      alt: en.sidebar.roleManagement,
      label: en.sidebar.roleManagement,
      tag: protectedRoutes?.find((route) => route.path === "/roles")?.tag,
    },
    {
      to: "/clients",
      icon: clientsIcon,
      alt: en.sidebar.clientManagement,
      label: en.sidebar.clientManagement,
      tag: protectedRoutes?.find((route) => route.path === "/clients")?.tag,
    },
    {
      to: "/campaigns",
      icon: campaignIcon,
      alt: en.sidebar.campaignManagement,
      label: en.sidebar.campaignManagement,
      tag: protectedRoutes?.find((route) => route.path === "/campaigns")?.tag,
    },
    {
      to: "/surveys",
      icon: surveyIcon,
      alt: en.sidebar.surveyManagement,
      label: en.sidebar.surveyManagement,
      tag: protectedRoutes?.find((route) => route.path === "/surveys")?.tag,
    },
    {
      to: "/receipt-management",
      icon: rewardIcon,
      alt: en.sidebar.receiptManagement,
      label: en.sidebar.receiptManagement,
      tag: protectedRoutes?.find(
        (route) => route.path === "/receipt-management"
      )?.tag,
    },
  ]);

  const preferencesMenuItems: MenuItem[] = filterMenuItems([
    {
      to: "/profile",
      icon: profileIcon,
      alt: en.sidebar.profile,
      label: en.sidebar.profile,
      tag: protectedRoutes?.find((route) => route.path === "/profile")?.tag,
    },
    // {
    //   to: "/settings",
    //   icon: settingsIcon,
    //   alt: en.sidebar.settings,
    //   label: en.sidebar.settings,
    //   tag: protectedRoutes?.find((route) => route.path === "/settings")?.tag,
    //   subMenu: [
    //     {
    //       to: "/settings/static-pages",
    //       icon: "",
    //       alt: "",
    //       label: en.settings.staticPages,
    //       tag: protectedRoutes?.find((route) => route.path === "/static-pages")
    //         ?.tag,
    //     },
    //   ],
    // },
    {
      to: "/login",
      icon: logoutIcon,
      alt: en.sidebar.logout,
      label: en.sidebar.logout,
    },
  ]);

  // If page is reloaded, set the expanded menu and active sub-menu by reading the URL
  useEffect(() => {
    const currentPathItems = currentPath.split("/").filter(item => item); // Remove empty strings

    if (currentPathItems.length === 1) {
      // Single-level paths like /surveys, /campaigns, /receipt-management, etc.
      setExpandedMenu(`/${currentPathItems[0]}`);
      setActiveSubMenu(null);
    } else if (currentPathItems.length >= 2) {
      // Multi-level paths like /dashboard/overview, /rewards/reward-settings, etc.
      setExpandedMenu(`/${currentPathItems[0]}`);
      setActiveSubMenu(`/${currentPathItems[0]}/${currentPathItems[1]}`);
    }
  }, [currentPath]);

  return (
    <nav className={styles.menu}>
      {/* Title Section */}
      <button
        className={`${styles.titleSection} btnTextOnly`}
        onClick={() => navigate("/dashboard/overview")}
      >
        <img src={appLogo} alt={en.sidebar.appName} />
        <h1>{en.sidebar.appName}</h1>
      </button>

      {/* General */}
      {generalMenuItems?.length > 0 && (
        <div className={styles.menuSectionTitle}>{en.sidebar.general}</div>
      )}
      <MenuList
        items={generalMenuItems}
        expandedMenu={expandedMenu}
        toggleMenu={toggleMenu}
        activeSubMenu={activeSubMenu}
      />

      {/* Management */}
      {managementMenuItems?.length > 0 && (
        <div className={styles.menuSectionTitle}>{en.sidebar.management}</div>
      )}
      <MenuList
        items={managementMenuItems}
        expandedMenu={expandedMenu}
        toggleMenu={toggleMenu}
        activeSubMenu={activeSubMenu}
      />

      {/* Preferences */}
      {preferencesMenuItems?.length > 0 && (
        <div className={styles.menuSectionTitle}>{en.sidebar.preferences}</div>
      )}

      <MenuList
        items={preferencesMenuItems}
        expandedMenu={expandedMenu}
        toggleMenu={toggleMenu}
        activeSubMenu={activeSubMenu}
      />

      <div className={styles.versionInfo}>
        Version: <span>{`0.2.2(20260328)`}</span>
      </div>
    </nav>
  );
};

export default SidebarMenu;
